import { useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/dataProvider/axios';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { ORG_MEMBER_USAGE_BUDGET_ENDPOINT } from '@/services/org-admin';
import type { MemberUsage, OrgUsageReport, OrgUsageRollup, SetMemberBudgetInput } from '@/services/org-admin';

export function useSetMemberBudget() {
  const queryClient = useQueryClient();
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);

  return useMutation({
    mutationFn: async ({ userId, input }: { userId: string; input: SetMemberBudgetInput }) => {
      const { data } = await axiosInstance.put<MemberUsage>(
        ORG_MEMBER_USAGE_BUDGET_ENDPOINT(orgId!, userId),
        input,
      );
      return { userId, seat: data };
    },
    onSuccess: ({ userId, seat }) => {
      // The PUT response already carries the fresh per-seat numbers — patch every
      // cached report window (7/15/30) and the rollup directly so the row updates
      // immediately, instead of waiting on a background refetch round trip.
      queryClient.setQueriesData<OrgUsageReport>({ queryKey: ['org-usage-report', orgId] }, (old) =>
        old
          ? {
              ...old,
              members: old.members.map((m) =>
                m.user_id === userId ? { ...m, budget_usd: seat.budget_usd, resets_at: seat.resets_at } : m,
              ),
            }
          : old,
      );
      queryClient.setQueriesData<OrgUsageRollup>({ queryKey: ['org-usage', orgId] }, (old) =>
        old
          ? {
              ...old,
              members: old.members.map((m) =>
                m.user_id === userId
                  ? {
                      ...m,
                      budget_usd: seat.budget_usd,
                      remaining_usd: seat.remaining_usd,
                      resets_at: seat.resets_at,
                      over_budget: seat.over_budget,
                    }
                  : m,
              ),
            }
          : old,
      );
      // Safety net for eventual consistency (e.g. spend also moved server-side).
      void queryClient.invalidateQueries({ queryKey: ['org-usage-report', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['org-usage', orgId] });
    },
    // Caller already shows its own success/error toast — suppress the global one.
    meta: { silent: true },
  });
}
